import React, { useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import StandardDetailLayout from '@/layouts/StandardDetailLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { joinFieldErrors } from "@/lib/form/FormFieldError";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const warehouseSchema = z.object({
  code: z.string().min(1, 'Bắt buộc nhập mã kho'),
  name: z.string().min(1, 'Bắt buộc nhập tên kho'),
  type: z.enum(['MATERIAL', 'FG', 'TECH', 'TOOLING', 'WIP', 'SCRAP', 'BRANCH', 'MAIN']),
  managerName: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
  notes: z.string().optional(),
  allowNegativeStock: z.boolean().default(true),
});

export interface WarehouseFormProps {
  id?: string;
  isModal?: boolean;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  initialData?: any;
  submitLabel?: string;
}

export function WarehouseForm({
  id: propId,
  isModal = false,
  onSuccess,
  onCancel,
  initialData: propInitialData,
  submitLabel,
}: WarehouseFormProps = {}) {
  const params = useParams({ strict: false }) as { id?: string };
  const effectiveId = propId || params?.id;
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const queryClient = useQueryClient();

  const isEdit = !!effectiveId && effectiveId !== 'new';

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['warehouses', 'getById', { id: effectiveId }],
    queryFn: () => trpcClient.warehouses.getById.query({ id: effectiveId as string }),
    enabled: isEdit && !propInitialData,
  });

  const activeInitialData = propInitialData || fetchedData;

  const createMutation = useMutation({
    mutationFn: (data: any) => trpcClient.warehouses.create.mutate(data),
    onSuccess: (res: any) => {
      toast.success('Tạo kho thành công');
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['combobox'] });
      queryClient.invalidateQueries({ queryKey: ['trpc', 'combobox', 'getOptions'] });
      if (onSuccess) {
        onSuccess({
          id: res.id,
          label: `${res.name} (${res.code})`,
          original: res,
        });
      } else if (!isModal) {
        navigate({ to: `/warehouse/master-data/${res.id}` } as any);
      }
    },
    onError: (err: any) => toast.error('Lỗi khi tạo kho: ' + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => trpcClient.warehouses.update.mutate(data),
    onSuccess: (res: any) => {
      toast.success('Cập nhật kho thành công');
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['combobox'] });
      queryClient.invalidateQueries({ queryKey: ['trpc', 'combobox', 'getOptions'] });
      if (onSuccess) {
        onSuccess({
          id: effectiveId as string,
          label: `${res?.name || form.getFieldValue('name')} (${res?.code || form.getFieldValue('code')})`,
          original: res,
        });
      } else if (!isModal) {
        navigate({ to: `/warehouse/master-data/${effectiveId}` } as any);
      }
    },
    onError: (err: any) => toast.error('Lỗi khi cập nhật kho: ' + err.message),
  });

  const form = useForm({
    defaultValues: {
      code: activeInitialData?.code || '',
      name: activeInitialData?.name || '',
      type: (activeInitialData?.type as any) || 'MATERIAL',
      managerName: activeInitialData?.managerName || '',
      phone: activeInitialData?.phone || '',
      address: activeInitialData?.address || '',
      status: (activeInitialData?.status as any) || 'ACTIVE',
      notes: activeInitialData?.notes || '',
      // Mặc định cho phép xuất âm (chạy test & golive); admin tắt theo từng kho
      allowNegativeStock: (activeInitialData as any)?.allowNegativeStock !== false,
    },
    onSubmit: async ({ value }) => {
      if (isEdit) {
        updateMutation.mutate({ id: effectiveId as string, ...value });
      } else {
        createMutation.mutate(value);
      }
    },
  });

  useEffect(() => {
    if (activeInitialData) {
      form.setFieldValue('code', activeInitialData.code || '');
      form.setFieldValue('name', activeInitialData.name || '');
      form.setFieldValue('type', (activeInitialData.type as any) || 'MATERIAL');
      form.setFieldValue('managerName', activeInitialData.managerName || '');
      form.setFieldValue('phone', activeInitialData.phone || '');
      form.setFieldValue('address', activeInitialData.address || '');
      form.setFieldValue('status', (activeInitialData.status as any) || 'ACTIVE');
      form.setFieldValue('notes', activeInitialData.notes || '');
      form.setFieldValue('allowNegativeStock', (activeInitialData as any).allowNegativeStock !== false);
    }
  }, [activeInitialData]);

  if (isEdit && isLoading && !propInitialData) {
    return <div className="p-8 text-center text-slate-500">Đang tải dữ liệu kho...</div>;
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const formBody = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-md border">
      {/* Section 1: Thông tin cơ bản */}
      <div className="space-y-4">
        <h3 className="font-medium text-slate-800 border-b pb-2">Thông tin cơ bản</h3>

        <form.Field
          name="code"
          validators={{ onChange: warehouseSchema.shape.code as any }}
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>
                Mã kho <span className="text-red-500">*</span>
              </Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="Ví dụ: KHO-VT-01"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.errors ? (
                <p className="text-xs text-red-500">{joinFieldErrors(field.state.meta.errors)}</p>
              ) : null}
            </div>
          )}
        />

        <form.Field
          name="name"
          validators={{ onChange: warehouseSchema.shape.name as any }}
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>
                Tên kho <span className="text-red-500">*</span>
              </Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="Ví dụ: Kho Nguyên Vật Liệu Chính"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.errors ? (
                <p className="text-xs text-red-500">{joinFieldErrors(field.state.meta.errors)}</p>
              ) : null}
            </div>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="type"
            children={(field: any) => (
              <div className="space-y-1">
                <Label>Loại kho</Label>
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Kho độc lập, mỗi kho một chức năng: không có kho tổng / chi nhánh; không quản kho BTP, phế liệu.
                        Loại cũ chỉ hiện khi kho đang mang loại đó. */}
                    {field.state.value === "MAIN" && <SelectItem value="MAIN">Kho tổng (cũ)</SelectItem>}
                    <SelectItem value="MATERIAL">Kho nguyên vật liệu</SelectItem>
                    <SelectItem value="FG">Kho thành phẩm</SelectItem>
                    {field.state.value === "WIP" && <SelectItem value="WIP">Kho bán thành phẩm (không quản)</SelectItem>}
                    <SelectItem value="TOOLING">Kho công cụ dụng cụ</SelectItem>
                    <SelectItem value="TECH">Kho vật tư kỹ thuật</SelectItem>
                    {field.state.value === "SCRAP" && <SelectItem value="SCRAP">Kho phế liệu (không quản)</SelectItem>}
                    {field.state.value === "BRANCH" && <SelectItem value="BRANCH">Kho chi nhánh (cũ)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <form.Field
            name="status"
            children={(field: any) => (
              <div className="space-y-1">
                <Label>Trạng thái</Label>
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Hoạt động</SelectItem>
                    <SelectItem value="INACTIVE">Tạm khóa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        </div>
      </div>

      {/* Section 2: Thông tin liên hệ */}
      <div className="space-y-4">
        <h3 className="font-medium text-slate-800 border-b pb-2">Liên hệ & Mở rộng</h3>

        <form.Field
          name="managerName"
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>Người quản lý</Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="Nhập tên thủ kho..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </div>
          )}
        />

        <form.Field
          name="phone"
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>Số điện thoại</Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="0912..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </div>
          )}
        />

        <form.Field
          name="address"
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>Địa chỉ kho</Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="Vị trí / Phân xưởng..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </div>
          )}
        />
      </div>

      <div className="col-span-full mt-2">
        <form.Field
          name="notes"
          children={(field: any) => (
            <div className="space-y-1">
              <Label htmlFor={field.name}>Ghi chú thêm</Label>
              <Input
                id={field.name}
                className="h-9"
                placeholder="Ghi chú về sức chứa, tiêu chuẩn lưu trữ..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </div>
          )}
        />
        <form.Field
          name="allowNegativeStock"
          children={(field: any) => (
            <label
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5 text-sm"
              title={isAdmin ? undefined : "Chỉ quản trị hệ thống được bật / tắt"}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={!!field.state.value}
                disabled={!isAdmin}
                onChange={(e) => field.handleChange(e.target.checked)}
                data-testid="warehouse-allow-negative"
              />
              <span>
                <span className="font-semibold text-slate-800">Cho phép xuất âm</span>
                <span className="block text-xs text-slate-600">
                  Xuất kho được khi tồn chưa đủ (số dư xuống dưới 0) — dùng khi chạy thử / golive, nhập bù sau. Chỉ quản trị hệ thống được bật / tắt.
                </span>
              </span>
            </label>
          )}
        />
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="space-y-4">
        {formBody}
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Hủy
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {submitLabel || (isEdit ? 'Lưu thay đổi' : 'Lưu kho hàng')}
          </Button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate({ to: '/warehouse/master-data' } as any);
    }
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: 'Danh mục Kho', to: '/warehouse/master-data' },
        { label: isEdit ? (activeInitialData?.name || 'Chỉnh sửa') : 'Thêm mới' },
      ]}
      title={isEdit ? `Sửa Kho ${activeInitialData?.code || activeInitialData?.name || ''}` : 'Thêm Mới Kho Hàng'}
      onBack={handleBack}
      showContextPanel={false}
      actions={
        <div className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-1.5" />
            )}
            {submitLabel || (isEdit ? 'Lưu thay đổi' : 'Lưu kho hàng')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 container mx-auto max-w-4xl p-4 md:p-6">

      {formBody}

      {/* FOOTER ACTIONS */}
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (onCancel) {
              onCancel();
            } else {
              navigate({ to: '/warehouse/master-data' } as any);
            }
          }}
          disabled={isSubmitting}
        >
          Hủy
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
          onClick={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1.5" />
          )}
          {submitLabel || (isEdit ? 'Lưu thay đổi' : 'Lưu kho hàng')}
        </Button>
      </div>
    </div>
  </StandardDetailLayout>
  );
}

export default WarehouseForm;
